import type { SoleCalcCopy } from "../../components/landing/sole-trader-calculator"
import type { SoleModelCopy } from "../../components/landing/sole-trader-calculator-model"
import type { SoleVisualCopy } from "../../components/landing/sole-trader-calculator-visual"

/**
 * Chinese text of the sole trader calculator (T1321). Terms: docs/design/landing/copy-zh.md. No rate, threshold
 * or amount is written here: numbers arrive as arguments from the engine or from the same constants as English.
 */
export const SOLE_MODEL_COPY_ZH: SoleModelCopy = {
  incomeEmpty: "请输入业务收入，查看该预留多少税款。",
  incomeBad: (max) => `请填写 0 至 ${max} 之间的金额，例如 100,000 或 85.50。`,
  expensesBad: (max) => `业务开支请填写 0 至 ${max} 之间的金额，没有就填 0。`,
  wagesBad: (max) => `工资请填写 0 至 ${max} 之间的金额，没有就填 0。`,
  hoursBad: "每周工时请填 1 至 100，例如 38。",
  daysBad: "每周工作天数请填 1 至 7，例如 5。",
  weeksBad: "全年工作周数请填整数周，范围 1 至 52。",
  outOfRange: "收入超出范围，请改小一些。",
  answer: (p) =>
    `按 ${p.income} 的 ABN（澳大利亚商业号码）收入和 ${p.expenses} 的开支计算，每月约需预留 ${p.perMonth} 用于缴纳 ${p.fy} 财年的税款（全年 ${p.total}，占收入的 ${p.share}%），税后约剩 ${p.takeHome}。估算包含${p.medicare ? "所得税和 Medicare levy（医疗保险税）" : "所得税"}，澳大利亚各州通用。`,
}

export const SOLE_CALC_COPY_ZH: SoleCalcCopy = {
  periods: { year: "年", month: "月", week: "周", day: "天", hour: "小时" },
  periodNoun: { year: "每年", month: "每月", week: "每周", day: "每天", hour: "每小时" },
  shows: { month: "每月", quarter: "每季度" },
  shortcuts: [
    { keys: ["↑", "↓"], separator: "/", action: "按一档调整收入" },
    { keys: ["Shift", "↑ ↓"], action: "调整十档" },
    { keys: ["Alt", "↑ ↓"], action: "调整十分之一档" },
    { keys: ["←", "→"], separator: "/", action: "在收入周期之间切换" },
    { keys: ["?"], action: "显示这些快捷键" },
    { keys: ["Esc"], action: "关闭此列表" },
  ],
  heading: "你的业务",
  paidLegend: "我的收入按",
  incomeLabel: (noun) => `业务收入（${noun}，不含 GST）`,
  hoursLabel: "每周工时",
  daysLabel: "每周工作天数",
  weeksLabel: "每年工作周数",
  expensesLabel: "业务开支（全年）",
  wagesLabel: "受雇工资（全年）",
  hint: "不含 GST。开支和工资都按全年填写。",
  moreOptions: "更多选项",
  moreOptionsDetail: "（财年、税务居民身份、HELP、Medicare）",
  incomeYear: "财年",
  yearOption: (y) => `${y} 财年`,
  residency: "税务居民身份",
  residencyOptions: {
    resident: "澳大利亚税务居民",
    foreign: "外国居民（foreign resident）",
    "working-holiday": "打工度假签证持有人（working holiday maker）",
  },
  helpBox: "我有 HELP（高等教育贷款）或其他学生贷款",
  medicareBox: (rate) => `计入 ${rate} 的 Medicare levy`,
  keysHint: { change: "调整收入", ten: "×10", all: "全部快捷键" },
  resultHeading: "需为税款预留",
  showLegend: "金额显示方式",
  headline: {
    month: (fy) => `${fy} 财年，每月需为税款预留`,
    quarter: (fy) => `${fy} 财年，每季度需为税款预留`,
    empty: "请输入收入",
    invalid: "请检查输入内容",
  },
  live: (headline, figure) => `${headline}：${figure}`,
  share: (ofIncome, ofProfit) => `约占收入的 ${ofIncome}，占利润的 ${ofProfit}`,
  noProfit: "没有利润，无需预留税款",
  tableCaption: "全年业务税款明细",
  rows: {
    income: "业务收入",
    expenses: "业务开支",
    profit: "利润",
    incomeTax: "所得税（已扣抵免）",
    medicare: "Medicare levy",
    help: "HELP 还款",
    taxYear: "全年需预留的税款",
    takeHome: "税后所得",
  },
  loss: "你的开支高于收入。计算器按无利润显示，也不会把亏损抵减你的工资收入。",
  provisional: "暂定。",
  provisionalFallback: "部分规则为暂定。",
  copyLink: "复制本结果的链接",
  copied: "链接已复制",
  copyFailed: "复制失败。请改为复制地址栏中的网址。",
  local: "在你的浏览器中运行，不会向我们发送任何数据。",
  disclaimerBefore:
    "仅为估算，不构成税务建议。按单身、无受抚养人、无其他扣除和抵免计算。未包含：小型企业所得税抵免（small business income tax offset）、PAYG instalments（预缴税款，金额由 ATO 通知你）、Medicare levy surcharge（医疗保险税附加费）和标准扣除额（standard deduction）。HELP 只按应税收入计算。你自己的报税请参考 ",
  disclaimerLink: "ATO 所得税估算器",
  disclaimerAfter: "，或咨询注册税务代理。",
  gstHeading: "GST",
  gstRequired: (c) =>
    `你的业务收入达到 ${c} 或以上，很可能需要登记 GST。出租车和网约车（ATO 称 ride-sourcing）司机不论营业额多少都必须登记。`,
  gstOptional: (c) =>
    `你的业务收入低于 ${c}，一般可以自愿选择是否登记 GST。GST 营业额一旦达到该门槛就必须登记；出租车和网约车（ATO 称 ride-sourcing）司机不论营业额多少都必须登记。`,
  gstUnknown: (c) => `GST 营业额一旦达到 ${c}，你就必须登记 GST。`,
  gstNote: "本计算器使用不含 GST 的收入，你代收的 GST 不算你的收入。来源：",
  gstLink: "ATO，GST 登记",
  gstNoteEnd: "。",
  superHeading: "Super（退休金）",
  superText: (c, fy) =>
    `本计算器假设没有雇主为你的业务收入缴纳 super。如果你自己向 super 存钱并申报扣除，这笔钱会和雇主为你的工资缴纳的 super 一起，计入 ${c} 的税前缴费上限（${fy} 财年）。本计算器不包含自愿缴费。`,
  superLink: "ATO，super 缴费上限",
  superLinkEnd: "。",
}

export const SOLE_VISUAL_COPY_ZH: SoleVisualCopy = {
  labels: {
    takeHome: "税后所得",
    expenses: "业务开支",
    tax: "所得税",
    medicare: "Medicare levy",
    help: "HELP 还款",
  },
  splitHeading: "你的业务收入花在哪里",
  showTable: "以表格显示",
  splitCaption: "全年业务收入的去向",
  part: "项目",
  amount: "金额",
  share: "占比",
  compareHeading: "同样的收入：ABN 个体经营者还是受雇员工",
  compareCaption: "全年税后所得对比：ABN 与收入相同的受雇员工",
  abn: "ABN（你）",
  employee: "受雇员工",
  rowBefore: "税前收入",
  rowExpenses: "业务开支",
  rowTax: "所得税及各项税款",
  rowTakeHome: "税后所得",
  rowSuper: "雇主另付的 super（退休金）",
  note: "受雇员工一栏把你的 ABN 收入当作工资：工资里不含 super，也没有工作开支。带薪假期等雇佣权益没有计价，所以这个对比只比较税和 super。",
}
